import React from 'react';
import { Box, Typography, Stack } from '@mui/material';
import { PhxBadge } from '_lex/_atoms/PhxBadge';
import type { GuideSheet } from 'views/App/Portal/Policy/_types/manuals/draft';
import parse from 'html-react-parser';
import { translate as t } from 'hooks/useTranslations';
import ActionMenu from 'components/_atoms/ActionMenu';
import CheckIcon from '@mui/icons-material/Check';
import { Edit } from '@mui/icons-material';
import { Button, CircularProgress } from '@mui/material';
import { useState } from 'react';
import { useSnackbar } from 'notistack';
import ExcalidrawImage from 'views/App/Portal/Policy/shared/Excalidraw';

interface GuideSheetDrawerProps {
  guideSheet?: GuideSheet;
  manualLevel?: string;
  onApproveGuideSheet?: () => Promise<void>;
  isEditing?: boolean;
}

const GuideSheetDrawer: React.FC<GuideSheetDrawerProps> = ({
  guideSheet,
  manualLevel,
  onApproveGuideSheet,
  isEditing,
}) => {
  const [approving, setApproving] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const extractExcalidrawData = (htmlContent: string): string[] => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
    const excalidrawElements = doc.querySelectorAll('[data-lexical-excalidraw-json]');

    const excalidrawDataArray: string[] = [];
    excalidrawElements.forEach((element) => {
      const data = element.getAttribute('data-lexical-excalidraw-json');
      if (data) {
        excalidrawDataArray.push(data);
      }
    });

    return excalidrawDataArray;
  };

  const renderContentWithExcalidraw = (htmlContent: string) => {
    const excalidrawDataArray = extractExcalidrawData(htmlContent);

    if (excalidrawDataArray.length === 0) {
      return parse(htmlContent);
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
    const excalidrawElements = doc.querySelectorAll('[data-lexical-excalidraw-json]');

    excalidrawElements.forEach((element, index) => {
      const placeholder = doc.createElement('div');
      placeholder.setAttribute('data-excalidraw-placeholder', index.toString());
      element.parentNode?.replaceChild(placeholder, element);
    });

    const parsedContent = parse(doc.body.innerHTML, {
      replace: (domNode: any) => {
        if (domNode.attribs && domNode.attribs['data-excalidraw-placeholder'] !== undefined) {
          const index = parseInt(domNode.attribs['data-excalidraw-placeholder']);
          const excalidrawData = excalidrawDataArray[index];

          if (excalidrawData) {
            return (
              <ExcalidrawImage
                key={`excalidraw-${index}`}
                excalidrawJson={excalidrawData}
                alt='Excalidraw diagram'
              />
            );
          }
        }
      },
    });

    return parsedContent;
  };

  const handleApprove = async () => {
    setApproving(true);
    try {
      if (onApproveGuideSheet) {
        await onApproveGuideSheet();
        enqueueSnackbar(
          t('draftManuals.guideSheet.statusSuccess', '', {
            status: t('draftManuals.guideSheet.approved'),
          }),
          { variant: 'success' }
        );
      }
    } catch (e: any) {
      console.error('GuideSheet approve error:', e);
      enqueueSnackbar(
        e?.message
          ? `${t('draftManuals.guideSheet.statusError')}: ${e.message}`
          : `${t('draftManuals.guideSheet.statusError')}: ${typeof e === 'string' ? e : JSON.stringify(e)}`,
        { variant: 'error' }
      );
    } finally {
      setApproving(false);
    }
  };
  return guideSheet ? (
    <Box sx={{ p: 0, pt: 2 }}>
      <Stack
        direction='row'
        alignItems='center'
        spacing={2}
        mb={2}
      >
        <Typography
          variant='bodySm'
          sx={{
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {guideSheet.title}
        </Typography>
        {manualLevel !== 'AGENCY' &&
          (isEditing ? (
            guideSheet.draftState !== 'APPROVED' ? (
              <ActionMenu
                id='guide-sheet-status-menu'
                btnText={t(`draftManuals.guideSheet.status.${guideSheet.draftState}`)}
                btnProps={{
                  variant: 'outlined',
                  color: 'neutral',
                  size: 'sm',
                  startIcon: approving ? (
                    <CircularProgress
                      size={20}
                      color='inherit'
                    />
                  ) : (
                    <Edit />
                  ),
                  disabled: approving,
                }}
                items={[
                  {
                    key: 'APPROVED',
                    label: t('draftManuals.guideSheet.approve'),
                    icon: <CheckIcon />,
                    onClick: handleApprove,
                    disabled: approving,
                  },
                ]}
              />
            ) : (
              <Button
                variant='outlined'
                color='neutral'
                startIcon={<CheckIcon />}
                sx={{
                  width: 140,
                  height: 32,
                  borderRadius: 2,
                  fontWeight: 600,
                  textTransform: 'none',
                  backgroundColor: 'background.paper',
                  color: 'text.primary',
                  border: '1.5px solid',
                  borderColor: 'divider',
                  justifyContent: 'flex-start',
                  px: 2,
                  alignItems: 'center',
                  display: 'flex',
                }}
                disabled
              >
                {t('draftManuals.guideSheet.approved')}
              </Button>
            )
          ) : (
            <PhxBadge
              color={guideSheet.draftState === 'APPROVED' ? 'success' : 'neutral'}
              size='sm'
            >
              {t(`draftManuals.guideSheet.status.title`)}:{' '}
              {t(`draftManuals.guideSheet.status.${guideSheet.draftState}`)}
            </PhxBadge>
          ))}
      </Stack>
      <Typography
        variant='bodyMd'
        component='div'
        sx={{
          overflowX: 'hidden',
          wordBreak: 'break-word',
          whiteSpace: 'pre-wrap',
        }}
        aria-label={t('draftManuals.guideSheet.contentAria')}
      >
        {renderContentWithExcalidraw(guideSheet.content || '')}
      </Typography>
    </Box>
  ) : (
    <Box sx={{ p: 0, pt: 2 }}>
      <Typography variant='subtitleMd'>{t('draftManuals.guideSheet.noGuideSheet')}</Typography>
    </Box>
  );
};

export type { GuideSheetDrawerProps };
export default GuideSheetDrawer;
